/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  CellStatuses,
  CellValue,
  DetailedCellError,
  Group,
  IdeasFile,
  IdeasFileNotFound,
  RoiFrame,
  RowIdentifier,
} from "@inscopix/ideas-hyperformula";
import { CellErrorLoading } from "../store/engine";
import { CellValueRendererLoading } from "./CellValueRendererLoading";
import { CellValueRendererError } from "./CellValueRendererError";
import { CellValueRendererFile } from "./CellValueRendererFile";
import { CellValueRendererRoiFrame } from "./CellValueRendererRoiFrame";
import { CellValueRendererRowIdentifier } from "./CellValueRendererRowIdentifier";
import { CellValueRendererCellStatuses } from "./CellValueRendererCellStatuses";
import { CellValueRendererGroup } from "./CellValueRendererGroup";
import { CellValueRendererFileNotFound } from "./CellValueRendererFileNotFound";
import { CellAddress } from "../store/DataTableProvider.types";

const styles = {
  root: css`
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  default: css`
    color: grey;
    font-style: italic;
  `,
};

interface CellValueRendererBaseProps {
  value: CellValue;
  isInGroup?: boolean;
  isLastValueInGroup?: boolean;
  isDefaultValue?: boolean;
  address?: CellAddress;
  isDraggable?: boolean;
  suppressFileSelection?: boolean;
  suppressFileContextMenu?: boolean;
}

/** Component that renders any cell value */
export const CellValueRendererBase = ({
  address,
  value,
  isInGroup = false,
  isLastValueInGroup = false,
  isDefaultValue = false,
  isDraggable = false,
  suppressFileContextMenu,
  suppressFileSelection,
}: CellValueRendererBaseProps) => {
  if (value instanceof DetailedCellError) {
    if (value.message === CellErrorLoading.message) {
      return <CellValueRendererLoading />;
    } else {
      return <CellValueRendererError error={value} />;
    }
  }

  if (value instanceof IdeasFile) {
    return (
      <CellValueRendererFile
        fileId={value.attrs.id}
        isCompact={isInGroup}
        address={address}
        isDraggable={isDraggable}
        suppressContextMenu={suppressFileContextMenu}
        suppressSelection={suppressFileSelection}
      />
    );
  }

  if (value === IdeasFileNotFound) {
    return <CellValueRendererFileNotFound isCompact={isInGroup} />;
  }

  if (value instanceof RoiFrame) {
    return <CellValueRendererRoiFrame roiFrame={value} />;
  }

  if (value instanceof RowIdentifier) {
    return <CellValueRendererRowIdentifier rowIdentifier={value} />;
  }

  if (value instanceof CellStatuses) {
    return <CellValueRendererCellStatuses cellStatuses={value} />;
  }

  if (value instanceof Group) {
    return (
      <CellValueRendererGroup
        address={address}
        group={value}
        isDraggable={isDraggable}
        suppressFileContextMenu={suppressFileContextMenu}
        suppressFileSelection={suppressFileSelection}
      />
    );
  }

  return (
    <span css={[styles.root, ...(isDefaultValue ? [styles.default] : [])]}>
      {value?.toString()}
      {isInGroup && !isLastValueInGroup ? "," : ""}
    </span>
  );
};
